import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';

import { EVENTO_SESSAO_ENCERRADA } from '../services/api';
import { FalhaTemporariaError, encerrarSessao, renovarSessao } from '../services/session';

const SEGUNDO = 1000;
const MINUTO = 60 * SEGUNDO;

const ANTECEDENCIA = 5 * MINUTO;
/** Espalha as abas abertas: cada uma renova num instante diferente. */
const JITTER_MAXIMO = 30 * SEGUNDO;
/** Com um token curto demais, evita renovar em laço. */
const ESPERA_MINIMA = MINUTO;
/** setTimeout estoura acima de ~24 dias; mais que um dia, só reagenda. */
const ESPERA_MAXIMA = 24 * 60 * MINUTO;
/** A primeira é curta para caber na janela de rotação do servidor (30 s). */
const NOVAS_TENTATIVAS = [5 * SEGUNDO, 30 * SEGUNDO, MINUTO, 2 * MINUTO];

function msAteVencer(): number {
  return (Number(Cookies.get('tokenExpiration')) || 0) * SEGUNDO - Date.now();
}

/**
 * Mantém a sessão viva enquanto o painel está aberto: renova 5 min antes do
 * token vencer e, se o servidor recusar, encerra a sessão guardando a tela de
 * origem. Rede fora não desloga: tenta de novo até conseguir.
 */
export function RenovadorDeSessao(): null {
  const history = useHistory();

  useEffect(() => {
    let ativo = true;
    let encerrado = false;
    let agendamento: number | undefined;

    // Depois de encerrar, nada mais renova: um segundo refresh recusado deslogava duas vezes.
    function encerrar() {
      if (encerrado) return;

      encerrado = true;
      window.clearTimeout(agendamento);
      encerrarSessao(history);
    }

    // O api.ts só encerra por conta própria se ninguém na tela tratar o evento.
    function aoEncerrarPorChamada(evento: Event) {
      evento.preventDefault();
      encerrar();
    }

    function agendar(espera: number, tentativa = 0) {
      window.clearTimeout(agendamento);
      agendamento = window.setTimeout(() => executar(tentativa), espera);
    }

    function agendarPeloVencimento() {
      const espera = msAteVencer() - ANTECEDENCIA - Math.random() * JITTER_MAXIMO;

      agendar(Math.min(Math.max(espera, ESPERA_MINIMA), ESPERA_MAXIMA));
    }

    async function executar(tentativa: number) {
      if (encerrado) return;

      // Outra aba (ou o 401 do api.ts) já renovou: o vencimento andou.
      if (msAteVencer() > ANTECEDENCIA + JITTER_MAXIMO) {
        agendarPeloVencimento();
        return;
      }

      try {
        await renovarSessao();

        if (ativo) agendarPeloVencimento();
      } catch (erro) {
        if (!ativo) return;

        if (erro instanceof FalhaTemporariaError) {
          const espera = NOVAS_TENTATIVAS[Math.min(tentativa, NOVAS_TENTATIVAS.length - 1)];

          agendar(espera, tentativa + 1);
          return;
        }

        encerrar();
      }
    }

    // Aba em segundo plano atrasa os timers: ao voltar, renova se o prazo apertou.
    function aoMudarDeVisibilidade() {
      if (document.visibilityState === 'visible' && msAteVencer() < ANTECEDENCIA) executar(0);
    }

    agendarPeloVencimento();
    document.addEventListener('visibilitychange', aoMudarDeVisibilidade);
    window.addEventListener(EVENTO_SESSAO_ENCERRADA, aoEncerrarPorChamada);

    return () => {
      ativo = false;
      window.clearTimeout(agendamento);
      document.removeEventListener('visibilitychange', aoMudarDeVisibilidade);
      window.removeEventListener(EVENTO_SESSAO_ENCERRADA, aoEncerrarPorChamada);
    };
  }, [history]);

  return null;
}
